// @core/stores/subscription/SubscriptionEntitlementStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { subscriptionApi } from '@core/api';
import type {
  CheckEntitlementResponse,
  Entitlement,
} from '@core/types/subscription';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SubscriptionEntitlementStore {
  // List state
  items: Entitlement[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Entitlement | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Entitlement>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Entitlement | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Entitlement | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Check feature entitlement
   */
  async CheckEntitlement(tenant_id: string): Promise<CheckEntitlementResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.CheckEntitlement(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get entitlement details
   */
  async GetEntitlement(entitlement_id: string): Promise<Entitlement> {
    // Check cache first
    const cached = this.cache.get(entitlement_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await subscriptionApi.GetEntitlement(entitlement_id);
      const item = response.entitlement;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List tenant entitlements
   */
  async ListEntitlements(tenant_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await subscriptionApi.ListEntitlements(tenant_id, params);

      runInAction(() => {
        this.items = response.entitlements?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const subscriptionEntitlementStore = new SubscriptionEntitlementStore();
